import { Star, Calculator, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';

const industries = [
  { name: 'Automotive', image: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=600&h=800&fit=crop' },
  { name: 'Retail & E-commerce', image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=800&fit=crop' },
  { name: 'Oil & Gas', image: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=600&h=800&fit=crop' },
  { name: 'Healthcare', image: 'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=600&h=800&fit=crop' },
  { name: 'Aerospace', image: 'https://images.unsplash.com/photo-1540962351504-03099e0a754b?w=600&h=800&fit=crop' },
  { name: 'Agriculture', image: 'https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=600&h=800&fit=crop' },
  { name: 'Consumer Goods', image: 'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?w=600&h=800&fit=crop' },
  { name: 'Construction', image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&h=800&fit=crop' },
];

const testimonials = [
  {
    quote: "Red Ball Express cut our shipping costs by 18% in the first quarter while improving on-time delivery. Their dispatch team is phenomenal — they actually pick up the phone.",
    author: "VP of Logistics",
    company: "Fortune 500 Retailer",
    metric: "18% cost reduction"
  },
  {
    quote: "We moved 400 loads in Q4 with zero claims. Their carrier vetting process and real-time tracking give us complete peace of mind.",
    author: "Operations Director",
    company: "Automotive Parts Manufacturer",
    metric: "400 loads, 0 claims"
  },
  {
    quote: "The 24/7 support isn't a marketing line — I've called at 2 AM on a Saturday and had a live dispatcher answer in under 30 seconds.",
    author: "Supply Chain Manager",
    company: "Aerospace Contractor",
    metric: "24/7 live support"
  },
  {
    quote: "We switched from a top-5 broker to Red Ball's own fleet and immediately noticed the difference. Smaller team, bigger hustle, better results.",
    author: "Director of Distribution",
    company: "National FMCG Brand",
    metric: "Better service quality"
  },
  {
    quote: "Their intermodal rates saved us $220K annually on our LA-to-Chicago lane. And the tracking visibility is better than our previous carrier's.",
    author: "Logistics Manager",
    company: "Consumer Electronics Company",
    metric: "$220K annual savings"
  },
];

export const QuoteCalculatorCTA = () => (
  <section className="relative py-20 overflow-hidden">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/67b0925b-7f4b-4cc2-900d-1c317dc2d0bd/images/c12958797443b539df5157e5329c365826f49437eb4625d06ff564aaa09a0652.png')` }}
    />
    <div className="absolute inset-0 bg-black/70" />
    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(90deg, #fff 0, #fff 1px, transparent 1px, transparent 80px)' }} />
    <div className="max-w-5xl mx-auto px-6 lg:px-8 relative text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
      <Calculator className="w-10 h-10 text-red-500 mx-auto mb-4" strokeWidth={1.5} />
      <h2 className="text-3xl sm:text-4xl font-black text-white mb-3 tracking-tight">
        Know Your Costs Before You Ship
      </h2>
      <p className="text-zinc-400 mb-8 text-lg max-w-2xl mx-auto" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        Our quote calculator gives you real-time estimates based on current market rates. No account needed. No obligation.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link
          to="/calculator"
          className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 font-bold uppercase tracking-wider text-sm transition-all hover:-translate-y-0.5"
          data-testid="calculator-cta-btn"
        >
          <Calculator className="w-5 h-5" />
          Try Quote Calculator
        </Link>
        <a
          href="tel:+18776809272"
          className="inline-flex items-center justify-center gap-2 border border-zinc-600 text-zinc-200 hover:border-white hover:text-white px-8 py-4 font-bold uppercase tracking-wider text-sm transition-colors"
          data-testid="call-cta-btn"
        >
          Call 1 (877) 680-9272
        </a>
      </div>
    </div>
  </section>
);

export const IndustriesSection = () => (
  <section className="py-24 bg-zinc-950 border-t border-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="text-center mb-14">
        <span className="text-red-500 text-xs font-bold uppercase tracking-[0.25em]">Industry Expertise</span>
        <h2 className="text-3xl sm:text-4xl font-black text-white mt-3 tracking-tight">
          Freight Solutions for Every Vertical
        </h2>
        <p className="text-zinc-500 mt-4 max-w-lg mx-auto" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          From perishable pharma to oversized steel — we've moved it all. Safely.
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {industries.map((ind, i) => (
          <motion.div
            key={ind.name}
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="relative aspect-[3/4] overflow-hidden group cursor-default"
            data-testid={`industry-${ind.name.toLowerCase().replace(/[&\s]+/g, '-')}`}
          >
            <img
              src={ind.image}
              alt={ind.name}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <h3 className="text-lg font-bold text-white">{ind.name}</h3>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const TestimonialsSection = () => (
  <section className="py-24 bg-zinc-950 border-t border-zinc-900 overflow-hidden" style={{ fontFamily: 'Outfit, sans-serif' }}>
    <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-12">
      <div className="text-center">
        <span className="text-red-500 text-xs font-bold uppercase tracking-[0.25em]">Client Results</span>
        <h2 className="text-3xl sm:text-4xl font-black text-white mt-3 tracking-tight">
          Don't Take Our Word for It
        </h2>
      </div>
    </div>
    <Marquee speed={35} gradient={false} pauseOnHover>
      {testimonials.map((t) => (
        <div
          key={t.company}
          className="mx-3 w-[380px] flex-shrink-0 bg-zinc-900/50 backdrop-blur-md border border-zinc-800 p-8 hover:border-zinc-700 transition-colors"
        >
          <div className="flex gap-0.5 mb-4">
            {[1,2,3,4,5].map(s => (
              <Star key={`star-${t.company}-${s}`} className="w-4 h-4 fill-amber-400 text-amber-400" />
            ))}
          </div>
          <div className="bg-red-600/10 text-red-500 text-xs font-bold uppercase tracking-wider px-3 py-1 inline-block mb-4">
            {t.metric}
          </div>
          <p className="text-zinc-300 mb-6 leading-relaxed text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            "{t.quote}"
          </p>
          <div className="border-t border-zinc-800 pt-4">
            <div className="text-white font-semibold text-sm">{t.author}</div>
            <div className="text-zinc-600 text-xs">{t.company}</div>
          </div>
        </div>
      ))}
    </Marquee>
  </section>
);
